// Warehouse delivery-time line-item-property annotator (HIG-122, Option C).
//
// Goal: every cart line gets ONE accurate `Verfügbarkeit` line-item property that
// reflects how the customer's quantity splits across delivery buckets — e.g.
// 7 of a product with 6 in CH + supplier →
//   "✓ 6 Stück sofort versandfertig · ● 1 Stück in 7 bis 9 Tagen".
// It's ONE cart line (no physical splitting) and the property carries through to
// checkout, order and invoice (the only Shopify mechanism that does).
//
// SAFE by design (vs. the abandoned line-splitting approach):
//  - We NEVER add or remove cart lines and never compute quantities — we only
//    SET the property text of an existing line, echoing that line's CURRENT
//    quantity back. So a stray concurrent change can NEVER double or change the
//    quantity. Worst case is a briefly stale text that self-corrects next pass.
//  - Debounced (reconcile only when the cart has been quiet ~400ms) so we never
//    run mid-burst, plus a fingerprint guard: we re-read right before writing and
//    abort if the cart changed since our snapshot.
//  - Reconcile is the SOLE author of `Verfügbarkeit` (the card/PDP just add the
//    item) — so there's only one place that formats it, no drift.
//
// Real per-bucket quantities come from window.__hpb (obfuscated — see
// snippets/hp-split-data.liquid). Lines whose product has no __hpb data are left
// untouched.

(function () {
  if (window.__warehouseDeliveryPropInstalled) return;
  window.__warehouseDeliveryPropInstalled = true;

  // Locale-aware cart root ('/' or '/en/' …). Posting to a non-prefixed /cart/change.js
  // on a localized page can 302-redirect (POST→GET) and return HTML. (HIG-141)
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // When the backend is overloaded/rate-limited (429/503 → HTML), back off instead of
  // hammering it with more cart requests (which is what makes the cart feel "broken").
  var backoffUntil = 0;

  // Throw on non-OK responses (429/503 → HTML error pages) so we never JSON.parse HTML.
  // Status-based, not content-type-based: the theme-preview proxy serves /cart.js as 200
  // with a non-JSON content-type but a valid JSON body, so a content-type check would
  // wrongly reject it. A 200 with a genuinely non-JSON body would reject in r.json()
  // (caught upstream, no backoff) — fine, it just skips that pass.
  function okJson(r) {
    if (!r.ok) {
      var err = new Error('hp-cart ' + r.status);
      err.httpStatus = r.status;
      throw err;
    }
    return r.json();
  }

  // HIG-140: the cart section emits the REAL vb of every current cart line as inline
  // JSON (data-hp-cart-vb). Merging it means lines added OUTSIDE a vb-emitting context
  // — no-variant direct-adds from the PDP, collection cards, sliders, search — still get
  // their Verfügbarkeit annotated (those skip the Choose-Options drawer that used to be
  // the only non-PDP vb source). Re-read every pass; the section re-renders on each change.
  function mergeCartVb() {
    var hpb = window.__hpb || (window.__hpb = {});
    var added = false;
    document.querySelectorAll('[data-hp-cart-vb]').forEach(function (el) {
      var arr;
      try { arr = JSON.parse(el.textContent || '[]'); } catch (e) { return; }
      if (!Array.isArray(arr)) return;
      arr.forEach(function (o) {
        if (!o || typeof o !== 'object') return;
        Object.keys(o).forEach(function (k) { if (!(k in hpb)) added = true; hpb[k] = o[k]; });
      });
    });
    // New delivery data arrived for a line that may already be in an unchanged cart —
    // invalidate the "stable" fingerprint guard so reconcile reprocesses it. Without this,
    // a line added before its vb was in the DOM would never get annotated. (HIG-140)
    if (added) lastFp = null;
  }

  // Property NAME is localized — it shows as the key in checkout/order/invoice, so
  // an English order must read "Availability", not "Verfügbarkeit". (HIG-122)
  // It's labelled "Verfügbarkeit" (availability), NOT "Lieferzeit" (delivery time):
  // what follows is the days-until-ready-to-ship, not the actual delivery time
  // (= next business day, shown in the shipping method). (Tobias 2026-06-07)
  var NAMES = { de: 'Verfügbarkeit', en: 'Availability', fr: 'Disponibilité', it: 'Disponibilità' };
  var ALL_NAMES = [NAMES.de, NAMES.en, NAMES.fr, NAMES.it];
  // Legacy property names (pre-2026-06-07 "Lieferzeit") — stripped on reconcile so
  // in-flight carts migrate cleanly to the new label, never showing both.
  var LEGACY_NAMES = ['Lieferzeit', 'Delivery time', 'Délai de livraison', 'Tempi di consegna'];
  var STRIP_NAMES = ALL_NAMES.concat(LEGACY_NAMES);
  var SEP = ', ';
  var QTY_CAP = 10; // never display a stock number above 10

  // Locale-aware label catalog (icon + label_lower per locale) — mirrors
  // assets/hp-collection-card.js LABEL_CATALOG.label_lower so the cart text reads
  // identically to the PDP/card delivery line. Keep in sync with that catalog.
  var LOCALE = (function () {
    var sl = (window.Shopify && window.Shopify.locale) || 'de';
    var s = String(sl).toLowerCase().split('-')[0];
    return ({ de: 1, en: 2, fr: 3, it: 4 })[s] ? s : 'de';
  })();
  var IDX = { de: 1, en: 2, fr: 3, it: 4 }[LOCALE];
  var PROP = NAMES[LOCALE] || NAMES.de;
  // [icon, de_lower, en_lower, fr_lower, it_lower]
  var L = {
    instant:        ['✓', 'sofort versandfertig', 'ready to ship', 'prêt à expédier', 'pronto per la spedizione'],
    in_1_2_days:    ['✓', 'versandfertig in 1 bis 2 Tagen', 'ready to ship in 1 to 2 days', 'prêt à expédier en 1 à 2 jours', 'pronto per la spedizione in 1-2 giorni'],
    in_2_3_days:    ['✓', 'versandfertig in 2 bis 3 Tagen', 'ready to ship in 2 to 3 days', 'prêt à expédier en 2 à 3 jours', 'pronto per la spedizione in 2-3 giorni'],
    in_3_4_days:    ['✓', 'versandfertig in 3 bis 4 Tagen', 'ready to ship in 3 to 4 days', 'prêt à expédier en 3 à 4 jours', 'pronto per la spedizione in 3-4 giorni'],
    in_4_5_days:    ['✓', 'versandfertig in 4 bis 5 Tagen', 'ready to ship in 4 to 5 days', 'prêt à expédier en 4 à 5 jours', 'pronto per la spedizione in 4-5 giorni'],
    in_5_6_days:    ['✓', 'versandfertig in 5 bis 6 Tagen', 'ready to ship in 5 to 6 days', 'prêt à expédier en 5 à 6 jours', 'pronto per la spedizione in 5-6 giorni'],
    in_6_7_days:    ['✓', 'versandfertig in 6 bis 7 Tagen', 'ready to ship in 6 to 7 days', 'prêt à expédier en 6 à 7 jours', 'pronto per la spedizione in 6-7 giorni'],
    in_7_9_days:    ['✓', 'versandfertig in 7 bis 9 Tagen', 'ready to ship in 7 to 9 days', 'prêt à expédier en 7 à 9 jours', 'pronto per la spedizione in 7-9 giorni'],
    in_10_14_days:  ['✓', 'versandfertig in 10 bis 14 Tagen', 'ready to ship in 10 to 14 days', 'prêt à expédier en 10 à 14 jours', 'pronto per la spedizione in 10-14 giorni'],
    in_15_20_days:  ['✓', 'versandfertig in 15 bis 20 Tagen', 'ready to ship in 15 to 20 days', 'prêt à expédier en 15 à 20 jours', 'pronto per la spedizione in 15-20 giorni'],
    in_3_4_weeks:   ['✓', 'versandfertig in 3 bis 4 Wochen', 'ready to ship in 3 to 4 weeks', 'prêt à expédier en 3 à 4 semaines', 'pronto per la spedizione in 3-4 settimane'],
    in_5_6_weeks:   ['✓', 'versandfertig in 5 bis 6 Wochen', 'ready to ship in 5 to 6 weeks', 'prêt à expédier en 5 à 6 semaines', 'pronto per la spedizione in 5-6 settimane'],
    in_7_9_weeks:   ['✓', 'versandfertig in 7 bis 9 Wochen', 'ready to ship in 7 to 9 weeks', 'prêt à expédier en 7 à 9 semaines', 'pronto per la spedizione in 7-9 settimane'],
    in_10_12_weeks: ['✓', 'versandfertig in 10 bis 12 Wochen', 'ready to ship in 10 to 12 weeks', 'prêt à expédier en 10 à 12 semaines', 'pronto per la spedizione in 10-12 settimane'],
    in_over_12_weeks: ['✓', 'versandfertig in über 12 Wochen', 'ready to ship in over 12 weeks', 'prêt à expédier en plus de 12 semaines', 'pronto per la spedizione in oltre 12 settimane'],
    over_12_weeks:  ['✓', 'versandfertig in über 12 Wochen (Datum in Abklärung)', 'ready to ship in over 12 weeks (date to be confirmed)', 'prêt à expédier en plus de 12 semaines (date à confirmer)', 'pronto per la spedizione in oltre 12 settimane (data da confermare)'],
    // «Produktion auf Bestellung» (Lieferzeit-Redesign 2026-07): Property-Text
    // ohne Stückzahl, mit Monats-Suffix aus dem vb-Bucket ([key, qty, mMin, mMax]).
    made_to_order:  ['✓', 'Produktion auf Bestellung', 'made to order', 'fabrication sur commande', 'produzione su ordinazione'],
  };
  // Monats-Suffix für made_to_order: ' (3-4 Monate)' — Wortlaut Tobias 2026-07-29.
  var MTO_UNIT_ALL = { de: 'Monate', en: 'months', fr: 'mois', it: 'mesi' };
  function mtoSuffixLoc(bdef, loc) {
    for (var i = 0; i < bdef.length; i++) {
      if (bdef[i][0] === 'made_to_order') {
        var mn = bdef[i][2], mx = bdef[i][3];
        if (mn == null && mx == null) return '';
        if (mn == null) mn = mx;
        if (mx == null) mx = mn;
        return ' (' + (mn === mx ? mn : mn + '-' + mx) + ' ' + MTO_UNIT_ALL[loc] + ')';
      }
    }
    return '';
  }
  var PIECE = { de: ['Stück', 'Stück'], en: ['piece', 'pieces'], fr: ['pièce', 'pièces'], it: ['pezzo', 'pezzi'] }[LOCALE];
  var MORE = { de: 'mehr als 10', en: 'more than 10', fr: 'plus de 10', it: 'più di 10' }[LOCALE];

  function capFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  // Numbered bucket (used when the quantity spans MULTIPLE buckets): "✓ 6 Stück sofort versandfertig".
  function renderBucket(k, q) {
    var e = L[k];
    if (!e) return '';
    var qtyText = q > QTY_CAP ? MORE : String(q);
    var unit = q === 1 ? PIECE[0] : PIECE[1];
    return e[0] + ' ' + qtyText + ' ' + unit + ' ' + e[IDX];
  }

  // Allocate `total` units across buckets fastest-first; overflow → slowest bucket.
  function allocate(total, bdef) {
    var out = [], rem = total;
    for (var i = 0; i < bdef.length; i++) {
      if (rem <= 0) break;
      var k = bdef[i][0], cap = bdef[i][1];
      var take = (i === bdef.length - 1) ? rem : Math.min(rem, cap);
      if (take > 0) { out.push([k, take]); rem -= take; }
    }
    return out;
  }
  // Per-locale lookup tables so we can render the property text in ANY language —
  // needed to recognise a property a DIFFERENT-language tab wrote (see the
  // any-language stability check in reconcileCart, Tobias 2026-06-09).
  var IDX_ALL = { de: 1, en: 2, fr: 3, it: 4 };
  var PIECE_ALL = { de: ['Stück', 'Stück'], en: ['piece', 'pieces'], fr: ['pièce', 'pièces'], it: ['pezzo', 'pezzi'] };
  var MORE_ALL = { de: 'mehr als 10', en: 'more than 10', fr: 'plus de 10', it: 'più di 10' };
  // Render the delivery text for `qty` in a specific locale.
  function deliveryTextLoc(qty, bdef, loc) {
    var alloc = allocate(qty, bdef);
    if (!alloc.length) return '';
    var idx = IDX_ALL[loc], piece = PIECE_ALL[loc], more = MORE_ALL[loc];
    // Single bucket (everything ships together) → NO number (qty already in the cart).
    if (alloc.length === 1) {
      var e = L[alloc[0][0]];
      if (!e) return '';
      var sfx = alloc[0][0] === 'made_to_order' ? mtoSuffixLoc(bdef, loc) : '';
      return e[0] + ' ' + capFirst(e[idx]) + sfx;
    }
    // Multiple buckets → numbered per bucket (made_to_order defensiv ohne Menge —
    // kommt als forced-Override real nie zusammen mit anderen Buckets vor).
    return alloc.map(function (a) {
      var e = L[a[0]]; if (!e) return '';
      if (a[0] === 'made_to_order') return e[0] + ' ' + capFirst(e[idx]) + mtoSuffixLoc(bdef, loc);
      var qtyText = a[1] > QTY_CAP ? more : String(a[1]);
      var unit = a[1] === 1 ? piece[0] : piece[1];
      return e[0] + ' ' + qtyText + ' ' + unit + ' ' + e[idx];
    }).filter(Boolean).join(SEP);
  }
  function deliveryText(qty, bdef) { return deliveryTextLoc(qty, bdef, LOCALE); }
  // Does `value` equal the quantity-correct delivery text in ANY supported language?
  function matchesAnyLocale(value, qty, bdef) {
    if (!value) return false;
    return ['de', 'en', 'fr', 'it'].some(function (loc) { return deliveryTextLoc(qty, bdef, loc) === value; });
  }

  // HIG-149 (instant cart delivery): expose the SAME formatter so the cart drawer can
  // render the delivery line immediately client-side from the already-present vb, instead
  // of waiting for the /cart/change property round-trip. Same data + same logic → the
  // instant display matches the property that's still written for checkout. Pulls the
  // freshest cart-section vb (mergeCartVb) first. Returns null if no vb for this variant.
  window.HPDeliveryText = function (variantId, qty) {
    try {
      mergeCartVb();
      var bdef = (window.__hpb || {})[String(variantId)];
      if (!bdef || !bdef.length) return null;
      return deliveryText(Number(qty) || 1, bdef) || null;
    } catch (e) { return null; }
  };
  // Bugfix 2026-07-30 (Mengenänderung im Drawer): Die Section rendert nach einem
  // Mengen-Klick die ALTE Property (der Property-Rewrite von reconcileCart kommt
  // erst Sekunden später und löst keinen Re-Render aus). hp-cart-delivery.js
  // prüft darum jede SSR-Zeile gegen die AKTUELLE Menge und rendert bei
  // Abweichung sofort client-seitig korrekt (gleiche Logik, gleiche Daten).
  window.HPDeliveryTextValid = function (variantId, qty, text) {
    try {
      mergeCartVb();
      var bdef = (window.__hpb || {})[String(variantId)];
      if (!bdef || !bdef.length) return true; // keine Daten → nichts zu validieren
      return matchesAnyLocale(String(text).trim(), Number(qty) || 1, bdef);
    } catch (e) { return true; }
  };

  // Lieferzeit-Redesign 2026-07: Die Cart-Kopfzeile zeigt das Zustellfenster des
  // LANGSAMSTEN Artikels im Korb. Dafür braucht hp-cart-delivery.js die effektiv
  // genutzten Buckets (Menge fastest-first alloziert) inkl. mto-Monaten.
  window.HPDeliveryBuckets = function (variantId, qty) {
    try {
      mergeCartVb();
      var bdef = (window.__hpb || {})[String(variantId)];
      if (!bdef || !bdef.length) return null;
      return allocate(Number(qty) || 1, bdef).map(function (a) {
        var out = { key: a[0], qty: a[1] };
        if (a[0] === 'made_to_order') {
          for (var i = 0; i < bdef.length; i++) {
            if (bdef[i][0] === 'made_to_order') { out.months_min = bdef[i][2]; out.months_max = bdef[i][3]; }
          }
        }
        return out;
      });
    } catch (e) { return null; }
  };

  function log() { try { console.log.apply(console, ['[hp-delivery-prop]'].concat([].slice.call(arguments))); } catch (e) {} }

  function fetchCart() {
    return fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(okJson);
  }
  // Change ONLY the properties of a line (echo its current quantity). /cart/change
  // replaces the line's properties, so we merge the existing ones (preserve gift
  // messages, BoostBundle, etc.) and just set/refresh Verfügbarkeit.
  function setLineProps(key, qty, props) {
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, properties: props })
    }).then(okJson);
  }

  function fingerprint(cart) {
    if (!cart || !cart.items) return '';
    return cart.items.map(function (i) {
      var p = Object.keys(i.properties || {}).sort().map(function (k) { return k + '=' + i.properties[k]; }).join(',');
      return i.key + 'x' + i.quantity + '[' + p + ']';
    }).join('|');
  }

  var reconciling = false, pending = false, lastFp = null;

  async function reconcileCart() {
    if (reconciling) { pending = true; return; }
    if (Date.now() < backoffUntil) return; // backend overloaded — wait it out
    reconciling = true;
    try {
      mergeCartVb(); // pull in any cart-line vb the section rendered (HIG-140)
      var cart = await fetchCart();
      var fp = fingerprint(cart);
      if (fp === lastFp && fp !== '') return; // stable → done
      var bmap = window.__hpb || {};

      // Decide the changes (do not apply yet).
      var changes = [];
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        var bdef = bmap[String(it.variant_id)];
        if (!bdef || !bdef.length) continue;          // no delivery data → leave alone
        var desired = deliveryText(it.quantity, bdef);
        if (!desired) continue;
        var cur = it.properties || {};
        // Any-language stability (Tobias 2026-06-09): keep a delivery property that a
        // DIFFERENT-language tab wrote, as long as it's still correct for the current
        // quantity. Only (re)write when there's nothing valid, the quantity changed,
        // a legacy name needs migrating, or there are duplicate names — so concurrent
        // multi-language tabs no longer flip-flop the cart every few seconds.
        var curNames = [];
        for (var n = 0; n < ALL_NAMES.length; n++) { if (cur[ALL_NAMES[n]] != null) curNames.push(ALL_NAMES[n]); }
        var hasLegacy = false;
        for (var lg = 0; lg < LEGACY_NAMES.length; lg++) { if (cur[LEGACY_NAMES[lg]] != null) { hasLegacy = true; break; } }
        var existingOk = curNames.length === 1 && !hasLegacy && matchesAnyLocale(cur[curNames[0]], it.quantity, bdef);
        if (existingOk) continue; // valid in some language for this qty → leave it (no flip)
        // Otherwise normalise to THIS tab's language (and clean up legacy/duplicates).
        var props = Object.assign({}, cur);
        for (var m = 0; m < STRIP_NAMES.length; m++) delete props[STRIP_NAMES[m]];
        props[PROP] = desired;
        delete props._hp_b; // drop legacy split marker if present
        changes.push({ key: it.key, quantity: it.quantity, properties: props });
      }
      if (!changes.length) { lastFp = fp; return; } // nothing to update → stable

      // Apply. The debounce already guarantees the cart was quiet (reconcile only
      // runs once the user paused), so we skip an extra guard re-read — fewer
      // round-trips = snappier. /cart/change returns the updated cart; we use the
      // last response for the fingerprint + drawer refresh.
      var resultCart = null;
      for (var c = 0; c < changes.length; c++) {
        resultCart = await setLineProps(changes[c].key, changes[c].quantity, changes[c].properties);
      }
      lastFp = (resultCart && resultCart.items) ? fingerprint(resultCart) : null;
      log('annotated', changes.length, 'line(s)');
      // Kein Drawer-Refresh mehr (2026-08-08): der Property-Write aendert nichts
      // Sichtbares — hp-cart-delivery aktualisiert die Lieferzeit-Texte ueber das
      // cart:updated unten in place. Der Refresh liess vorher saemtliche Bilder
      // im Drawer neu aufbauen (Flackern).
      if (resultCart && resultCart.items) document.dispatchEvent(new CustomEvent('cart:updated', { detail: resultCart }));
    } catch (e) {
      console.warn('[hp-delivery-prop] failed:', e);
      var st = e && e.httpStatus;
      if (st === 429 || st === 502 || st === 503 || st === 504 || /non-json|DOCTYPE/i.test(String(e && e.message))) {
        backoffUntil = Date.now() + 20000; // overloaded/rate-limited → stop hammering for 20s
      }
    } finally {
      reconciling = false;
      if (pending) { pending = false; setTimeout(reconcileCart, 50); }
    }
  }

  // Debounce: coalesce rapid changes but stay snappy. 150ms is safe here because C
  // is structurally race-proof (property-only, no add/remove, + fingerprint guard);
  // the long 400ms was only needed for the abandoned line-splitting approach.
  var debounceTimer = null;
  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcileCart, 150);
  }

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function urlPath(u) { try { return new URL(u, location.origin).pathname; } catch (e) { return String(u || '').split('?')[0]; } }
  function isCartMutation(u) { return CART_MUTATION_RE.test(urlPath(u)); }

  var origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);
    var res = origFetch(input, init);
    try { if (isCartMutation(url)) res.then(scheduleReconcile, function () {}); } catch (e) {}
    return res;
  };
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (m, u) { this.__hpUrl = u; return origOpen.apply(this, arguments); };
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (b) {
    try { if (isCartMutation(this.__hpUrl)) this.addEventListener('loadend', scheduleReconcile); } catch (e) {}
    return origSend.apply(this, arguments);
  };
  ['cart:item-added', 'cart:updated', 'cart:refresh', 'cart:change', 'cart:line-item-updated'].forEach(function (evt) {
    document.addEventListener(evt, scheduleReconcile);
    window.addEventListener(evt, scheduleReconcile);
  });
  setInterval(reconcileCart, 3000); // slow safety net

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileCart);
  } else {
    reconcileCart();
  }
  log('delivery-property annotator installed');
})();
